import { VARIANT_PRELOAD_WIDTHS } from './image-utils.js';

/**
 * Reactive Image URL Management System
 *
 * Centralized, computed image URL generation for the gallery builder.
 *
 * Key Features:
 * - Automatic URL computation based on artwork state
 * - Smart caching with LRU eviction
 * - Preload management with deduplication
 * - Performance tracking and metrics
 *
 * @module ReactiveImageUrls
 */

/**
 * Configuration constants for performance and behavior tuning
 */
const CONFIG = {
  PRELOAD_DEBOUNCE_TIMEOUT: 200,
  MAX_CACHE_SIZE: 20,
  PRIORITY_BATCH_SIZE: 3,
  LAZY_OBSERVER_ROOT_MARGIN: '120px',
  LAZY_OBSERVER_THRESHOLD: 0.15
};

/**
 * Image size configuration constants
 * Centralized sizing for consistency across the application
 */
export const IMAGE_SIZES = {
  thumbnail: 160,
  vertical: 630,
  horizontal: 872,
  mobile_display: 536,
  large: 1200,
  // Preload sizes for different contexts
  preload: {
    gallery: [536, 630, 872, 160],
    modal: [536, 630, 872, 150],
    carousel: [160, 320]
  }
};

/**
 * Create reactive image URL manager
 * Designed to be mixed into Alpine store
 *
 * @returns {Object} Image URL management methods and properties
 */
export function createImageUrlManager() {
  return {
    // Image URL cache with smart invalidation
    _imageUrlCache: new Map(),
    _imageUrlCacheVersion: 0,
    _lastImageDeps: null,
    _imagePreloadTimeout: null,
    _lazyObserver: null,
    _lazyObserverInitialized: false,
    _pendingLazyRefresh: null,

    // Image performance metrics
    _imageMetrics: {
      urlComputations: 0,
      cacheHits: 0,
      cacheMisses: 0,
      preloadedCount: 0,
      preloadTime: 0,
      partialInvalidations: 0,
      fullInvalidations: 0,
      lazyObserverInit: 0,
      lazyObserverFallback: 0,
      lazyRefreshes: 0,
      priorityPreloadCalls: 0,
      priorityPreloadImages: 0,
      lastPriorityPreloadDuration: 0,
      manualInvalidations: 0,
      lastLazyRefreshObserved: 0
    },

    /**
     * Computed property for all artwork image URLs
     * Single source of truth for image URLs
     * @returns {Map<string, Object>} Map of artwork IDs to URL objects
     */
    get artworkImageUrls() {
      // Ensure we have required data
      if (!this.selected_artworks || this.selected_artworks.length === 0) {
        return new Map();
      }

      const cache_key = this._generateImageCacheKey();

      // Check cache first
      if (this._imageUrlCache.has(cache_key)) {
        this._imageMetrics.cacheHits++;
        return this._imageUrlCache.get(cache_key);
      }

      this._imageMetrics.cacheMisses++;
      this._imageMetrics.urlComputations++;

      const urlMap = new Map();

      for (const artwork of this.selected_artworks) {
        if (!artwork?.id) continue;

        const urls = this._computeArtworkUrls(artwork);
        urlMap.set(artwork.id, urls);
      }

      // Cache the result with size limit
      this._cacheImageUrls(cache_key, urlMap);

      return urlMap;
    },

    /**
     * Get image URLs for specific artwork
     * @param {string|Object} artworkIdOrObject - Artwork ID or artwork object
     * @returns {Object} URL object with display, thumbnail, horizontal, vertical, large, zoom, srcset, preload, source, baseUrl, hasVariant, orientation properties
     */
    getArtworkUrls(artworkIdOrObject) {
      // Handle both ID and object being passed
      let artworkId, artwork;

      if (typeof artworkIdOrObject === 'object' && artworkIdOrObject !== null) {
        artwork = artworkIdOrObject;
        artworkId = artwork.id;
      } else {
        artworkId = artworkIdOrObject;
      }

      const urlMap = this.artworkImageUrls;

      // Check cache first if we have a Map
      if (urlMap && typeof urlMap.get === 'function') {
        const cachedUrls = urlMap.get(artworkId);
        if (cachedUrls) {
          return cachedUrls;
        }
      }

      // If we don't have the artwork object yet, try to find it
      if (!artwork) {
        // Try to find in selected artworks first
        artwork = this.selected_artworks?.find(a => a?.id === artworkId);

        // If not in selected, check all available artworks
        if (!artwork && this.all_products) {
          artwork = this.all_products.find(a => a?.id === artworkId);
        }
      }

      // If we have the artwork (either passed in or found), compute its URLs
      if (artwork) {
        const computedUrls = this._computeArtworkUrls(artwork);
        return computedUrls;
      }

      // Fallback to placeholders
      return getPlaceholderUrls(this.orientation || 'horizontal');
    },

    /**
     * Mark a specific artwork's image URLs as stale so they recompute on next access
     * @param {string} artworkId - The ID of the artwork to invalidate
     * @returns {void}
     */
    markArtworkImageDirty(artworkId) {
      if (!artworkId) return;
      const changed = this._invalidateArtworkUrls([artworkId]);
      if (changed) {
        this._triggerLazyLoading();
        this._requestHighPriorityPreload(artworkId);
      }
    },

    /**
     * Mark all artwork image URLs dirty (manual trigger)
     * @returns {void}
     */
    markAllArtworkImagesDirty() {
      this._invalidateImageUrlCache('manual');
      this._triggerLazyLoading();
    },

    /**
     * Request the lazy loader to observe current artwork nodes
     * @returns {void}
     */
    refreshLazyImageTargets() {
      this._triggerLazyLoading();
    },

    /**
     * Initialize image URL watchers
     * Called from store init()
     * @returns {void}
     */
    initImageUrlWatchers() {
      // Ensure Alpine is available
      if (typeof Alpine === 'undefined') {
        return;
      }

      this._initLazyLoading();
      if (typeof Alpine.nextTick === 'function') {
        Alpine.nextTick(() => {
          this._scheduleLazyRefresh();
        });
      }

      Alpine.effect(() => {
        const deps = this._getImageUrlDependencies();
        const depsString = JSON.stringify(deps);

        if (this._lastImageDeps !== depsString) {
          if (this._lastImageDeps !== null) {
            this._onImageDependenciesChanged();
          }
          this._lastImageDeps = depsString;
        }
      });
    },

    /**
     * Get dependencies that affect image URLs
     * @private
     * @returns {Object} Object containing artworkIds, orientation, variantSize, variantFrame, variantMount, variantLocked, previewCount
     */
    _getImageUrlDependencies() {
      // Safe access with fallbacks
      const artworks = this.selected_artworks || [];
      const variant = this.variant_selection || {};
      const previews = this.preview_variants || [];

      return {
        artworkIds: artworks.map(a => a?.id).filter(Boolean).join(','),
        orientation: this.orientation || 'horizontal',
        variantSize: variant.size || null,
        variantFrame: variant.frame || null,
        variantMount: variant.mount || null,
        variantLocked: variant.locked || false,
        previewCount: previews.length
      };
    },

    /**
     * Handle image dependency changes
     * @private
     * @returns {void}
     */
    _onImageDependenciesChanged() {
      this._invalidateImageUrlCache();

      // Debounce preloading
      clearTimeout(this._imagePreloadTimeout);
      this._imagePreloadTimeout = setTimeout(() => {
        this._preloadCurrentImages('normal');
      }, CONFIG.PRELOAD_DEBOUNCE_TIMEOUT);

      this._triggerLazyLoading();
    },

    /**
     * Trigger lazy loading initialization and refresh
     * @private
     */
    _triggerLazyLoading() {
      this._initLazyLoading();
      this._scheduleLazyRefresh();
    },

    /**
     * Check if running in browser environment
     * @private
     * @returns {boolean}
     */
    _isBrowserEnvironment() {
      return typeof window !== 'undefined' && typeof document !== 'undefined';
    },

    /**
     * Check if preload methods are available
     * @private
     * @returns {boolean}
     */
    _arePreloadMethodsAvailable() {
      return typeof this.isImagePreloaded === 'function' && typeof this.preloadImageBatch === 'function';
    },

    /**
     * Generate cache key for current state
     * @private
     * @returns {string} Cache key string
     */
    _generateImageCacheKey() {
      // Safe access with fallbacks
      const variant = this.variant_selection || {};
      return `v${this._imageUrlCacheVersion}-${this.orientation || 'horizontal'}-${variant.size || 'none'}-${variant.frame || 'none'}-${variant.mount || 'none'}`;
    },

    /**
     * Cache image URLs with size limit
     * @private
     * @param {string} key - Cache key
     * @param {Map} urlMap - Map of URLs to cache
     * @returns {void}
     */
    _cacheImageUrls(key, urlMap) {
      this._imageUrlCache.set(key, urlMap);

      // LRU eviction - remove oldest entries
      if (this._imageUrlCache.size > CONFIG.MAX_CACHE_SIZE) {
        const firstKey = this._imageUrlCache.keys().next().value;
        this._imageUrlCache.delete(firstKey);
      }
    },

    /**
     * Compute all URLs for an artwork
     * @private
     * @param {Object} artwork - Artwork object
     * @returns {Object} URL object with all computed URLs
     */
    _computeArtworkUrls(artwork) {
      const source = this._getImageSource(artwork);
      const orientation = this.orientation || 'horizontal';

      if (!source) {
        return getPlaceholderUrls(orientation);
      }

      const baseUrl = source.split('?')[0];

      return {
        // Display URLs
        display: getDisplayUrl(baseUrl, orientation),
        thumbnail: `${baseUrl}?width=${IMAGE_SIZES.thumbnail}`,
        horizontal: `${baseUrl}?width=${IMAGE_SIZES.horizontal}`,
        vertical: `${baseUrl}?width=${IMAGE_SIZES.vertical}`,
        large: `${baseUrl}?width=${IMAGE_SIZES.large}`,
        zoom: baseUrl,

        // Responsive srcset
        srcset: generateSrcSet(baseUrl),

        // Preload URLs
        preload: this._getPreloadUrls(baseUrl, artwork),

        // Metadata
        source: source,
        baseUrl: baseUrl,
        hasVariant: this._hasVariantImage(artwork),
        orientation: orientation
      };
    },

    /**
     * Get the best image source for an artwork
     * @private
     * @param {Object} artwork - Artwork object
     * @returns {string|null} Best image source URL or null
     */
    _getImageSource(artwork) {
      const variantSelection = this.variant_selection || {};
      const previewVariants = this.preview_variants || [];

      // 1. Locked variant configuration (highest priority)
      if (variantSelection.locked && variantSelection.size && typeof this.findVariantForArtwork === 'function') {
        const variant = this.findVariantForArtwork(artwork);
        if (variant?.featured_image?.src) {
          return variant.featured_image.src;
        }
      }

      // 2. Preview variant (for non-locked selections)
      const previewVariant = previewVariants.find(v => v?.product_id === artwork.id);
      if (previewVariant?.featured_image?.src) {
        return previewVariant.featured_image.src;
      }

      // 3. Selected variant (may be stale from previous selection)
      if (artwork.selected_variant?.featured_image?.src) {
        return artwork.selected_variant.featured_image.src;
      }

      // 4. Base image source
      if (artwork._base_image_source) {
        return artwork._base_image_source;
      }

      // 5. Final fallback
      if (artwork.featured_image?.src) {
        return artwork.featured_image.src;
      }

      return null;
    },

    /**
     * Check if artwork has variant image
     * @private
     * @param {Object} artwork - Artwork object
     * @returns {boolean} True if artwork has variant image
     */
    _hasVariantImage(artwork) {
      const previewVariants = this.preview_variants || [];
      return Boolean(
        artwork.selected_variant?.featured_image?.src ||
        previewVariants.find(v => v?.product_id === artwork.id)?.featured_image?.src
      );
    },

    /**
     * Get URLs to preload for an artwork
     * @private
     * @param {string} baseUrl - Base URL for the artwork
     * @param {Object} artwork - Artwork object
     * @returns {string[]} Array of URLs to preload
     */
    _getPreloadUrls(baseUrl, artwork) {
      const urls = new Set();
      const orientation = this.orientation || 'horizontal';
      const selectedArtworks = this.selected_artworks || [];

      const addWidth = (width) => {
        if (typeof width === 'number') {
          urls.add(`${baseUrl}?width=${width}`);
        }
      };

      // Current orientation and thumbnail
      addWidth(IMAGE_SIZES[orientation]);
      addWidth(IMAGE_SIZES.thumbnail);

      // Shared variant preload widths (includes mobile display size)
      VARIANT_PRELOAD_WIDTHS.forEach(addWidth);

      // Both orientations for selected artworks
      if (selectedArtworks.includes(artwork)) {
        addWidth(IMAGE_SIZES.horizontal);
        addWidth(IMAGE_SIZES.vertical);

        // Modal sizes if open (safely check for Alpine store)
        if (typeof Alpine !== 'undefined' && Alpine.store) {
          const galleryUI = Alpine.store('galleryUI');
          if (galleryUI?.variant_modal_open) {
            addWidth(150);
          }
        }
      }

      return Array.from(urls);
    },

    /**
     * Invalidate cache entries for specific artworks
     * @private
     * @param {string[]} artworkIds - Array of artwork IDs to invalidate
     * @returns {boolean} Whether any cache entries were removed
     */
    _invalidateArtworkUrls(artworkIds = []) {
      if (!Array.isArray(artworkIds) || artworkIds.length === 0) {
        return false;
      }

      const ids = artworkIds
        .map(id => id !== null ? String(id) : null)
        .filter(Boolean);

      if (ids.length === 0) {
        return false;
      }

      let invalidatedKeys = 0;

      this._imageUrlCache.forEach((urlMap, key) => {
        const hasMatch = ids.some(id => urlMap.has(id));
        if (hasMatch) {
          this._imageUrlCache.delete(key);
          invalidatedKeys++;
        }
      });

      if (invalidatedKeys > 0) {
        this._imageUrlCacheVersion++;
        this._imageMetrics.partialInvalidations += invalidatedKeys;
        return true;
      }

      return false;
    },

    /**
     * Invalidate entire image URL cache
     * @private
     * @param {string} reason - Reason for invalidation ('auto' or 'manual')
     * @returns {void}
     */
    _invalidateImageUrlCache(reason = 'auto') {
      const hadEntries = this._imageUrlCache.size > 0;
      this._imageUrlCacheVersion++;
      if (hadEntries) {
        this._imageUrlCache.clear();
        this._imageMetrics.fullInvalidations++;
        if (reason === 'manual') {
          this._imageMetrics.manualInvalidations =
            (this._imageMetrics.manualInvalidations || 0) + 1;
        }
      }
    },

    /**
     * Preload images from computed URLs
     * @private
     * @param {string} priority - Priority level ('normal' or 'high')
     * @returns {Promise<void>}
     */
    async _preloadCurrentImages(priority = 'normal') {
      // Check if preloading methods are available
      if (!this._arePreloadMethodsAvailable()) {
        return;
      }

      const startTime = performance.now();
      const urls = new Set();
      const orientation = this.orientation || 'horizontal';

      // Gather URLs from computed properties
      for (const urlObj of this.artworkImageUrls.values()) {
        if (urlObj.preload && Array.isArray(urlObj.preload)) {
          for (const url of urlObj.preload) {
            urls.add(url);
          }
        }
      }

      const urlArray = Array.from(urls);
      const uncached = urlArray.filter(url =>
        !this.isImagePreloaded(url, orientation)
      );

      if (uncached.length === 0) {
        return;
      }

      // Delegate to existing preload system
      if (priority === 'high') {
        await this.preloadImageBatch(uncached.slice(0, CONFIG.PRIORITY_BATCH_SIZE), orientation);
        if (uncached.length > CONFIG.PRIORITY_BATCH_SIZE) {
          await this.preloadImageBatch(uncached.slice(CONFIG.PRIORITY_BATCH_SIZE), orientation);
        }
      } else {
        await this.preloadImageBatch(uncached, orientation);
      }

      const elapsed = performance.now() - startTime;
      this._imageMetrics.preloadedCount += uncached.length;
      this._imageMetrics.preloadTime += elapsed;
    },

    /**
     * Initialize lazy loading via IntersectionObserver
     * @private
     * @returns {void}
     */
    _initLazyLoading() {
      if (this._lazyObserverInitialized) {
        return;
      }

      if (!this._isBrowserEnvironment()) {
        this._imageMetrics.lazyObserverFallback++;
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this._imageMetrics.lazyObserverFallback++;
        return;
      }

      this._lazyObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const target = entry.target;
          const artworkId = target?.getAttribute('data-artwork-id');
          if (artworkId) {
            void this._priorityPreload(artworkId);
          }

          if (this._lazyObserver) {
            this._lazyObserver.unobserve(entry.target);
          }
        }
      }, {
        rootMargin: CONFIG.LAZY_OBSERVER_ROOT_MARGIN,
        threshold: CONFIG.LAZY_OBSERVER_THRESHOLD
      });

      this._lazyObserverInitialized = true;
      this._imageMetrics.lazyObserverInit++;
      this._scheduleLazyRefresh();
    },

    /**
     * Schedule a DOM scan to attach lazy observers
     * @private
     * @returns {void}
     */
    _scheduleLazyRefresh() {
      if (!this._lazyObserverInitialized || !this._lazyObserver) {
        return;
      }
      if (!this._isBrowserEnvironment()) {
        return;
      }
      if (this._pendingLazyRefresh) {
        return;
      }

      const scheduler = (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function')
        ? window.requestAnimationFrame.bind(window)
        : (callback) => setTimeout(() => callback(Date.now()), 16);

      this._pendingLazyRefresh = scheduler(() => {
        this._pendingLazyRefresh = null;

        const nodes = document.querySelectorAll('[data-artwork-id]');
        let observedCount = 0;

        for (const node of nodes) {
          if (!node) continue;
          const artworkId = node.getAttribute('data-artwork-id');
          if (!artworkId) continue;
          this._lazyObserver.observe(node);
          observedCount++;
        }

        this._imageMetrics.lazyRefreshes++;
        this._imageMetrics.lastLazyRefreshObserved = observedCount;
      });
    },

    /**
     * Trigger a priority preload for artwork currently needed
     * @private
     * @param {string} artworkId - ID of the artwork to preload
     * @returns {Promise<void>}
     */
    async _priorityPreload(artworkId) {
      if (!artworkId) return;
      if (!this._arePreloadMethodsAvailable()) {
        return;
      }

      const urls = this.getArtworkUrls(artworkId);
      if (!urls?.preload || urls.preload.length === 0) {
        return;
      }

      const orientation = this.orientation || 'horizontal';
      const uncached = urls.preload.filter(url => !this.isImagePreloaded(url, orientation));
      if (uncached.length === 0) {
        return;
      }

      const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();

      this._imageMetrics.priorityPreloadCalls++;
      this._imageMetrics.priorityPreloadImages += uncached.length;

      try {
        await this.preloadImageBatch(uncached, orientation);
      } finally {
        const endTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
        this._imageMetrics.lastPriorityPreloadDuration = endTime - startTime;
      }
    },

    /**
     * Schedule a priority preload without blocking current execution
     * @private
     * @param {string} artworkId - ID of the artwork to preload
     * @returns {void}
     */
    _requestHighPriorityPreload(artworkId) {
      if (!artworkId) return;
      Promise.resolve().then(() => this._priorityPreload(artworkId));
    },

    /**
     * Get image performance report
     * @returns {Object} Performance metrics object
     */
    getImagePerformanceReport() {
      const hitRate = this._imageMetrics.cacheHits + this._imageMetrics.cacheMisses > 0
        ? (this._imageMetrics.cacheHits /
           (this._imageMetrics.cacheHits + this._imageMetrics.cacheMisses) * 100).toFixed(2)
        : 0;

      return {
        urlComputations: this._imageMetrics.urlComputations,
        cacheHits: this._imageMetrics.cacheHits,
        cacheMisses: this._imageMetrics.cacheMisses,
        cacheHitRate: `${hitRate}%`,
        preloadedCount: this._imageMetrics.preloadedCount,
        avgPreloadTime: this._imageMetrics.preloadedCount > 0
          ? `${(this._imageMetrics.preloadTime / this._imageMetrics.preloadedCount).toFixed(2)}ms`
          : '0ms',
        urlCacheSize: this._imageUrlCache.size,
        preloadCacheSize: this.preload_cache?.size || 0,
        partialInvalidations: this._imageMetrics.partialInvalidations,
        fullInvalidations: this._imageMetrics.fullInvalidations,
        manualInvalidations: this._imageMetrics.manualInvalidations,
        lazyObserverInit: this._imageMetrics.lazyObserverInit,
        lazyObserverFallback: this._imageMetrics.lazyObserverFallback,
        lazyRefreshes: this._imageMetrics.lazyRefreshes,
        lastLazyRefreshObserved: this._imageMetrics.lastLazyRefreshObserved,
        priorityPreloadCalls: this._imageMetrics.priorityPreloadCalls,
        priorityPreloadImages: this._imageMetrics.priorityPreloadImages,
        lastPriorityPreloadDuration: `${this._imageMetrics.lastPriorityPreloadDuration.toFixed(2)}ms`
      };
    }
  };
}

/**
 * Helper Functions
 * Pure functions that don't depend on store state
 */

/**
 * Get display URL for orientation
 */
export function getDisplayUrl(baseUrl, orientation) {
  const width = IMAGE_SIZES[orientation] || IMAGE_SIZES.horizontal;
  return `${baseUrl}?width=${width}`;
}

/**
 * Generate responsive srcset
 */
export function generateSrcSet(baseUrl) {
  const sizes = [160, 320, 630, 872, 1200];
  return sizes.map(size => `${baseUrl}?width=${size} ${size}w`).join(', ');
}

/**
 * Get placeholder URLs
 */
export function getPlaceholderUrls(orientation = 'horizontal') {
  const dimensions = orientation === 'horizontal' ? '872x600' : '630x900';
  const [width, height] = dimensions.split('x');

  const placeholder = `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"%3E%3Crect width="100%25" height="100%25" fill="%23f0f0f0"/%3E%3C/svg%3E`;

  return {
    display: placeholder,
    thumbnail: placeholder,
    horizontal: placeholder,
    vertical: placeholder,
    large: placeholder,
    zoom: placeholder,
    srcset: '',
    preload: [],
    source: null,
    baseUrl: null,
    hasVariant: false,
    orientation
  };
}
